import { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CircleNotch, Sparkle, CheckCircle, WarningCircle, Receipt, ArrowsClockwise, Scales, BookOpenText, PencilSimpleLine, Bank, Wallet, Factory, UploadSimple, CaretDown, CaretRight, ArrowRight, ClockCounterClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CoachStatementUploadDialog } from "@/components/CoachStatementUploadDialog";

const statusTone = {
  healthy: { bg: "var(--bk-success-bg)", fg: "var(--bk-success)", label: "Tied out" },
  ready_to_close: { bg: "#ECFDF3", fg: "#15803D", label: "Ready to close" },
  needs_review: { bg: "#FFF7E8", fg: "#B45309", label: "Review pending" },
  off: { bg: "#FDECE8", fg: "#B23B1F", label: "Difference to fix" },
  needs_statement: { bg: "#EEF2FF", fg: "#4338CA", label: "Need a statement" },
  nothing_yet: { bg: "#F6F3ED", fg: "var(--bk-text-2)", label: "Nothing recorded yet" },
};

const sectionMeta = {
  cash_cards_loans: { Icon: Scales, eyebrow: "Cash • cards • loans" },
  ar: { Icon: Wallet, eyebrow: "Accounts receivable" },
  ap: { Icon: Bank, eyebrow: "Accounts payable" },
  equity: { Icon: Sparkle, eyebrow: "Owner equity" },
  fixed_assets: { Icon: Factory, eyebrow: "Fixed assets" },
};

const metricTone = {
  default: "var(--bk-text)",
  success: "var(--bk-success)",
  warning: "#B45309",
};

const timelineTone = {
  income: { fg: "var(--bk-success)", bg: "rgba(34,197,94,0.10)" },
  expense: { fg: "#B23B1F", bg: "rgba(178,59,31,0.10)" },
  transfer: { fg: "var(--bk-text-2)", bg: "rgba(26,39,68,0.06)" },
};

const money = (amount, kind) => {
  const prefix = kind === "income" ? "+" : (kind === "expense" ? "−" : "");
  return `${prefix}$${Math.abs(Number(amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function CoachMetric({ label, value, testId, tone = "var(--bk-text)" }) {
  return (
    <div className="bk-card p-5" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{label}</p>
      <p className="font-display text-2xl font-bold mt-2" style={{ color: tone }}>{value}</p>
    </div>
  );
}

function ProgressRing({ percent, testId }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference - (Math.max(0, Math.min(100, percent || 0)) / 100) * circumference;
  return (
    <div className="relative h-24 w-24 shrink-0" data-testid={testId}>
      <svg viewBox="0 0 84 84" className="h-24 w-24 -rotate-90">
        <circle cx="42" cy="42" r={radius} fill="none" stroke="rgba(26,39,68,0.12)" strokeWidth="8" />
        <circle cx="42" cy="42" r={radius} fill="none" stroke="var(--bk-accent)" strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dash} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{Math.round(percent || 0)}%</span>
      </div>
    </div>
  );
}

function SectionHeader({ section }) {
  const meta = sectionMeta[section.key] || { Icon: Sparkle, eyebrow: "Balance sheet section" };
  const sectionProgress = section.progress || { percent: 0, completed_steps: 0, total_steps: 0 };
  return (
    <div className="rounded-[28px] p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(255,249,240,0.95), rgba(255,255,255,0.98))", border: "1px solid rgba(229,231,235,0.85)" }} data-testid={`bs-coach-section-header-${section.key}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(232,168,56,0.12),transparent_26%)]" />
      <div className="relative flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <meta.Icon size={18} weight="duotone" color="var(--bk-accent)" />
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }} data-testid={`bs-coach-section-eyebrow-${section.key}`}>{meta.eyebrow}</p>
          </div>
          <h4 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }} data-testid={`bs-coach-section-title-${section.key}`}>{section.title}</h4>
          <p className="text-sm mt-3" style={{ color: "var(--bk-text)" }} data-testid={`bs-coach-section-rowan-${section.key}`}>{section.rowan_intro}</p>
          <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid={`bs-coach-section-plain-english-${section.key}`}>{section.plain_english}</p>
        </div>
        <div className="flex items-center gap-4 rounded-[24px] px-4 py-3 bg-white/75 backdrop-blur-sm" style={{ border: "1px solid rgba(229,231,235,0.9)" }}>
          <ProgressRing percent={sectionProgress.percent} testId={`bs-coach-section-ring-${section.key}`} />
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Section progress</p>
            <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }}>{sectionProgress.completed_steps}/{sectionProgress.total_steps} checklist steps done</p>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }} data-testid={`bs-coach-section-summary-${section.key}`}>{section.rowan_summary}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BalanceSheetCoach({ businessId, permissions = {}, onOpenReconcile, onOpenReports, onOpenJournal }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [uploadTarget, setUploadTarget] = useState(null);
  const [openTimelines, setOpenTimelines] = useState({});
  const [openHistoryEntries, setOpenHistoryEntries] = useState({});
  const [exportingHistory, setExportingHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/businesses/${businessId}/accounting/balance-sheet-coach?as_of=${asOf}`);
      setData(payload);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load the reconciliation coach.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, asOf]);

  const progress = useMemo(() => data?.progress || { completed_steps: 0, total_steps: 20, percent: 0, healthy_accounts: 0, total_accounts: 0, ready_sections: 0, total_sections: 5 }, [data]);
  const sections = data?.sections || [];
  const history = data?.reconciliation_history || { entries: [], empty_state: "No reconciliation history yet. Let's get your first month closed — then you'll see the magic here." };

  const openTab = (tab) => navigate(`/business/${businessId}?tab=${tab}`);

  const openLikelyTransaction = (item) => {
    if (item.kind === "transaction") {
      navigate(`/business/${businessId}?tab=books&txn=${item.id}`);
      return;
    }
    if (item.kind === "journal") {
      onOpenJournal?.();
      return;
    }
    if (item.kind === "tab" && item.target_tab) {
      openTab(item.target_tab);
      return;
    }
    onOpenReports?.();
  };

  const primaryActionFor = (account) => {
    if (account.recommended_view === "reconcile") return { label: "Open reconcile", onClick: () => onOpenReconcile?.(account), testId: `bs-coach-primary-reconcile-${account.account_id}`, Icon: Scales };
    if (account.recommended_view === "ar") return { label: "Open A/R", onClick: () => openTab("ar"), testId: `bs-coach-primary-ar-${account.account_id}`, Icon: Wallet };
    if (account.recommended_view === "ap") return { label: "Open A/P", onClick: () => openTab("ap"), testId: `bs-coach-primary-ap-${account.account_id}`, Icon: Bank };
    if (account.recommended_view === "journal") return { label: "Open journal", onClick: () => onOpenJournal?.(account), testId: `bs-coach-primary-journal-${account.account_id}`, Icon: PencilSimpleLine };
    return { label: "Open reports", onClick: () => onOpenReports?.(account), testId: `bs-coach-primary-reports-${account.account_id}`, Icon: BookOpenText };
  };

  const openUploadDialog = (account) => setUploadTarget(account);
  const toggleTimeline = (accountId) => setOpenTimelines((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  const openTimelineTransaction = (item) => navigate(`/business/${businessId}?tab=books&txn=${item.id}`);
  const toggleHistoryEntry = (periodKey) => setOpenHistoryEntries((prev) => ({ ...prev, [periodKey]: !prev[periodKey] }));

  const exportHistoryPdf = async () => {
    setExportingHistory(true);
    try {
      const response = await api.get(`/businesses/${businessId}/accounting/reconciliation-history/export-pdf`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `reconciliation-history-${businessId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't export the reconciliation history PDF.");
    } finally {
      setExportingHistory(false);
    }
  };

  const handleStatementUploaded = async () => {
    await load();
  };

  if (loading) {
    return <div className="bk-card p-8 flex items-center gap-3" data-testid="balance-sheet-coach-loading"><CircleNotch size={18} className="animate-spin" /> Loading Rowan’s reconciliation plan…</div>;
  }

  return (
    <div className="space-y-8" data-testid="balance-sheet-coach">
      <div className="bk-rowan-card p-6" data-testid="balance-sheet-coach-rowan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Balance Sheet Reconciliation Coach</p>
            <h4 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>The full balance sheet — explained like a normal human.</h4>
            <p className="text-sm mt-3" style={{ color: "var(--bk-text)" }}>{data?.rowan_intro}</p>
            <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="balance-sheet-coach-plain-english">{data?.plain_english}</p>
          </div>
          <div className="flex items-center gap-2">
            <DateInput value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="balance-sheet-coach-as-of" className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)" }} />
            <button onClick={load} data-testid="balance-sheet-coach-refresh" className="px-4 py-2 rounded-full border text-sm font-semibold inline-flex items-center gap-2" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              <ArrowsClockwise size={15} weight="bold" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CoachMetric label="Checklist progress" value={`${progress.percent}%`} testId="bs-coach-progress-card" tone="var(--bk-accent)" />
        <CoachMetric label="Accounts tied out" value={`${progress.healthy_accounts}/${progress.total_accounts}`} testId="bs-coach-healthy-card" tone="var(--bk-success)" />
        <CoachMetric label="Sections stable" value={`${progress.ready_sections}/${progress.total_sections}`} testId="bs-coach-sections-card" />
      </div>

      <div className="bk-card p-5" data-testid="bs-coach-overall-summary-card">
        <div className="flex items-center gap-2 mb-3">
          <Receipt size={18} weight="duotone" color="var(--bk-accent)" />
          <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Rowan’s read on the whole balance sheet</h4>
        </div>
        <p className="text-sm" style={{ color: "var(--bk-text)" }} data-testid="bs-coach-summary-card">{data?.rowan_summary || "—"}</p>
      </div>

      {sections.map((section) => {
        const sectionProgress = section.progress || { percent: 0, healthy_accounts: 0, total_accounts: 0 };
        return (
          <div key={section.key} className="space-y-5" data-testid={`bs-coach-section-${section.key}`}>
            <SectionHeader section={section} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CoachMetric label="Checklist progress" value={`${sectionProgress.percent}%`} testId={`bs-coach-progress-${section.key}`} tone="var(--bk-accent)" />
              <CoachMetric label="Accounts tied out" value={`${sectionProgress.healthy_accounts}/${sectionProgress.total_accounts}`} testId={`bs-coach-healthy-${section.key}`} tone="var(--bk-success)" />
              <CoachMetric label="Rowan’s read" value={section.rowan_summary || "—"} testId={`bs-coach-summary-${section.key}`} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[0.82fr_1.18fr] gap-6">
              <div className="bk-card p-5" data-testid={`bs-coach-checklist-card-${section.key}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkle size={18} weight="fill" color="var(--bk-gold)" />
                  <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Rowan’s checklist</h4>
                </div>
                <div className="space-y-4">
                  {(section.checklist || []).map((item) => (
                    <div key={item.id} className="rounded-2xl border p-4" style={{ borderColor: item.done ? "rgba(34,197,94,0.25)" : "var(--bk-border)", background: item.done ? "#F5FBF6" : "#fff" }} data-testid={`bs-coach-step-${section.key}-${item.id}`}>
                      <div className="flex items-start gap-3">
                        {item.done ? <CheckCircle size={18} weight="fill" color="var(--bk-success)" className="shrink-0 mt-0.5" /> : <WarningCircle size={18} weight="fill" color="#B45309" className="shrink-0 mt-0.5" />}
                        <div>
                          <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{item.title}</p>
                          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{item.detail}</p>
                          <p className="text-xs mt-2" style={{ color: "var(--bk-accent)" }}>{item.rowan_note}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-5" data-testid={`bs-coach-account-list-${section.key}`}>
                {(section.accounts || []).map((account) => {
                  const tone = statusTone[account.status] || statusTone.off;
                  const primaryAction = primaryActionFor(account);
                  const PrimaryIcon = primaryAction.Icon;
                  const showUploadNudge = ["needs_statement", "awaiting_statement"].includes(account.status);
                  const timeline = account.balance_change_timeline || { items: [], rowan_header: "Here's what moved the needle this month:", dominant_note: "", period_label: "this month" };
                  const timelineOpen = !!openTimelines[account.account_id];
                  return (
                    <div key={account.account_id} className="bk-card p-5" data-testid={`bs-coach-account-${section.key}-${account.account_id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{account.name}</h4>
                            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: tone.bg, color: tone.fg }} data-testid={`bs-coach-status-${section.key}-${account.account_id}`}>{account.status_label || tone.label}</span>
                          </div>
                          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{account.account_type} · {account.detail_type}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {showUploadNudge && (
                            <button onClick={() => openUploadDialog(account)} data-testid={`bs-coach-upload-statement-${section.key}-${account.account_id}`} className="px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors" style={{ background: "rgba(67,56,202,0.08)", color: "#4338CA" }}>
                              <UploadSimple size={14} weight="bold" /> Upload statement →
                            </button>
                          )}
                          <button onClick={primaryAction.onClick} data-testid={primaryAction.testId} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><PrimaryIcon size={14} weight="bold" /> {primaryAction.label}</button>
                          {account.recommended_view !== "reports" && <button onClick={() => onOpenReports?.(account)} data-testid={`bs-coach-open-reports-${section.key}-${account.account_id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}><BookOpenText size={14} weight="bold" /> Open reports</button>}
                          {account.adjusting_entries?.length > 0 && account.recommended_view !== "journal" && <button onClick={() => onOpenJournal?.(account)} data-testid={`bs-coach-open-journal-${section.key}-${account.account_id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5"><PencilSimpleLine size={14} weight="bold" /> Draft adjustment</button>}
                        </div>
                      </div>

                      <p className="text-sm mt-4" style={{ color: "var(--bk-text)" }} data-testid={`bs-coach-rowan-${section.key}-${account.account_id}`}>{account.rowan_message}</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        {(account.metrics || []).map((metric, idx) => (
                          <div key={`${account.account_id}-${idx}`} className="rounded-2xl border p-4" style={{ borderColor: metric.tone === "warning" ? "#FCE7C6" : metric.tone === "success" ? "rgba(34,197,94,0.24)" : "var(--bk-border)" }} data-testid={`bs-coach-metric-${section.key}-${account.account_id}-${idx}`}>
                            <p className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{metric.label}</p>
                            <p className="font-display text-xl font-bold mt-2" style={{ color: metricTone[metric.tone || "default"] || metricTone.default }}>{metric.value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 rounded-2xl border" style={{ borderColor: "var(--bk-border)", background: "rgba(255,255,255,0.9)" }} data-testid={`bs-coach-timeline-${section.key}-${account.account_id}`}>
                        <button onClick={() => toggleTimeline(account.account_id)} data-testid={`bs-coach-timeline-toggle-${section.key}-${account.account_id}`} className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left">
                          <div>
                            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Why did this balance change?</p>
                            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{timeline.period_label} · largest movers first</p>
                          </div>
                          {timelineOpen ? <CaretDown size={16} weight="bold" color="var(--bk-text-2)" /> : <CaretRight size={16} weight="bold" color="var(--bk-text-2)" />}
                        </button>
                        {timelineOpen && (
                          <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-timeline-body-${section.key}-${account.account_id}`}>
                            <p className="text-sm font-semibold mt-4" style={{ color: "var(--bk-text)" }} data-testid={`bs-coach-timeline-header-${section.key}-${account.account_id}`}>{timeline.rowan_header}</p>
                            {timeline.dominant_note && <p className="text-xs mt-2" style={{ color: "var(--bk-accent)" }} data-testid={`bs-coach-timeline-dominant-${section.key}-${account.account_id}`}>{timeline.dominant_note}</p>}
                            {(timeline.items || []).length === 0 ? (
                              <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }}>No direct transaction movement hit this account this month yet.</p>
                            ) : (
                              <div className="space-y-3 mt-4">
                                {timeline.items.map((item) => {
                                  const itemTone = timelineTone[item.impact_kind] || timelineTone.transfer;
                                  return (
                                    <button key={item.id} onClick={() => openTimelineTransaction(item)} data-testid={`bs-coach-timeline-item-${section.key}-${account.account_id}-${item.id}`} className="w-full rounded-xl border p-3 flex items-center justify-between gap-3 text-left transition-transform hover:scale-[1.01]" style={{ borderColor: "var(--bk-border)" }}>
                                      <div className="min-w-0">
                                        <p className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{item.date}</p>
                                        <p className="text-sm font-semibold mt-1 truncate" style={{ color: "var(--bk-text)" }}>{item.description}</p>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: itemTone.fg, background: itemTone.bg }}>{money(item.amount, item.impact_kind)}</span>
                                        <ArrowRight size={14} weight="bold" color="var(--bk-text-2)" />
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-5 mt-5">
                        <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-likely-transactions-${section.key}-${account.account_id}`}>
                          <h5 className="text-sm font-semibold mb-3" style={{ color: "var(--bk-text)" }}>Likely activity worth checking</h5>
                          {(account.likely_transactions || []).length === 0 ? (
                            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Nothing obvious popped yet. That can mean this section is empty on purpose — or that the support still needs to be entered.</p>
                          ) : (
                            <div className="space-y-3">
                              {account.likely_transactions.map((item) => (
                                <div key={`${item.kind}-${item.id}`} className="rounded-xl border p-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-activity-${section.key}-${item.kind}-${item.id}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{item.label}</p>
                                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{item.date} · {item.kind === "journal" ? "Manual journal" : item.direction}</p>
                                      <p className="text-xs mt-2" style={{ color: "var(--bk-accent)" }}>{item.reason}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-sm font-semibold" style={{ color: item.direction === "income" ? "var(--bk-success)" : item.kind === "journal" ? "var(--bk-text)" : "#B23B1F" }}>{`$${(Number(item.amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
                                      <button onClick={() => openLikelyTransaction(item)} data-testid={`bs-coach-open-activity-${section.key}-${item.id}`} className="text-xs font-semibold mt-2 underline underline-offset-4" style={{ color: "var(--bk-accent)" }}>{item.kind === "transaction" ? "Open in Books" : item.kind === "journal" ? "Open Journal" : item.target_tab === "ar" ? "Open A/R" : "Open A/P"}</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-adjustments-${section.key}-${account.account_id}`}>
                          <h5 className="text-sm font-semibold mb-3" style={{ color: "var(--bk-text)" }}>Possible adjusting entries</h5>
                          {(account.adjusting_entries || []).length === 0 ? (
                            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No clean-up entry suggestion needed here right now. If this section is empty or tied out, that is the win.</p>
                          ) : (
                            <div className="space-y-4">
                              {account.adjusting_entries.map((entry, idx) => (
                                <div key={`${account.account_id}-${idx}`} className="rounded-xl border p-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-adjustment-${section.key}-${account.account_id}-${idx}`}>
                                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{entry.title}</p>
                                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{entry.why}</p>
                                  <div className="mt-3 space-y-2">
                                    {entry.lines.map((line, lineIdx) => (
                                      <div key={lineIdx} className="flex items-center justify-between text-xs" data-testid={`bs-coach-adjustment-line-${section.key}-${account.account_id}-${idx}-${lineIdx}`}>
                                        <span style={{ color: "var(--bk-text)" }}>{line.side} · {line.account}</span>
                                        <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{line.amount_hint ? `$${(Number(line.amount_hint) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Use actual support"}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <p className="text-xs mt-3" style={{ color: "#8A6A3B" }}>{entry.caution}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`bs-coach-glossary-${section.key}-${account.account_id}`}>
                        {(account.glossary || []).map((item) => (
                          <div key={item.term} className="rounded-full px-3 py-2 text-xs" style={{ background: "var(--bk-page)", color: "var(--bk-text)" }}>
                            <strong>{item.term}:</strong> {item.plain_english}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      <div className="space-y-4" data-testid="bs-coach-reconciliation-history-section">
        <div className="bk-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <ClockCounterClockwise size={18} weight="duotone" color="var(--bk-accent)" />
                <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Reconciliation History</h4>
              </div>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>Month-by-month close notes, fixes, and Rowan’s recap of what happened.</p>
            </div>
            {permissions?.can_export_reports !== false && (history.entries || []).length > 0 && (
              <button onClick={exportHistoryPdf} disabled={exportingHistory} data-testid="bs-coach-history-export-pdf" className="px-4 py-2 rounded-full border text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                {exportingHistory ? <><CircleNotch size={14} className="animate-spin" /> Exporting…</> : <><DownloadSimple size={14} weight="bold" /> Export history as PDF</>}
              </button>
            )}
          </div>
        </div>

        {(history.entries || []).length === 0 ? (
          <div className="bk-rowan-card p-6" data-testid="bs-coach-history-empty-state">
            <p className="text-sm" style={{ color: "var(--bk-text)" }}>{history.empty_state}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.entries.map((entry) => {
              const isOpen = !!openHistoryEntries[entry.period_key];
              return (
                <div key={entry.period_key} className="bk-card p-5" data-testid={`bs-coach-history-entry-${entry.period_key}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h5 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{entry.period_label}</h5>
                      <div className="flex flex-wrap gap-2 mt-3 text-xs font-semibold">
                        <span className="px-3 py-1.5 rounded-full" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`bs-coach-history-accounts-${entry.period_key}`}>{entry.accounts_reconciled_count} accounts reconciled</span>
                        <span className="px-3 py-1.5 rounded-full" style={{ background: "#FFF7E8", color: "#B45309" }} data-testid={`bs-coach-history-found-${entry.period_key}`}>{entry.issues_found_count} issues found</span>
                        <span className="px-3 py-1.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid={`bs-coach-history-resolved-${entry.period_key}`}>{entry.issues_resolved_count} resolved</span>
                      </div>
                    </div>
                    {entry.is_closed && <span className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "#ECFDF3", color: "#15803D" }}>Closed</span>}
                  </div>
                  <p className="text-sm mt-4" style={{ color: "var(--bk-text)" }} data-testid={`bs-coach-history-summary-${entry.period_key}`}>{entry.rowan_summary}</p>
                  <button onClick={() => toggleHistoryEntry(entry.period_key)} data-testid={`bs-coach-history-toggle-${entry.period_key}`} className="mt-4 text-sm font-semibold inline-flex items-center gap-2" style={{ color: "var(--bk-accent)" }}>
                    {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />} View details
                  </button>
                  {isOpen && (
                    <div className="mt-4 space-y-3" data-testid={`bs-coach-history-details-${entry.period_key}`}>
                      {(entry.resolved_issues || []).length === 0 ? (
                        <div className="rounded-2xl border p-4 text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>No resolved line-item fixes were logged for this month.</div>
                      ) : (
                        entry.resolved_issues.map((issue, idx) => (
                          <div key={`${entry.period_key}-${idx}`} className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`bs-coach-history-issue-${entry.period_key}-${idx}`}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{issue.fix_type}</p>
                                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{issue.date} · {issue.account_name}</p>
                              </div>
                              <span className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>${(Number(issue.amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }}>{issue.description}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CoachStatementUploadDialog
        open={!!uploadTarget}
        onOpenChange={(open) => { if (!open) setUploadTarget(null); }}
        businessId={businessId}
        account={uploadTarget}
        onUploaded={handleStatementUploaded}
      />
    </div>
  );
}